'use client';

import Box from '@enlighten/design-system/dist/Box';
import Button from '@enlighten/design-system/dist/Button';
import Checkbox from '@enlighten/design-system/dist/Checkbox';
import CircularProgress from '@enlighten/design-system/dist/CircularProgress';
import FormControlLabel from '@enlighten/design-system/dist/FormControlLabel';
import FormHelperText from '@enlighten/design-system/dist/FormHelperText';
import Grid from '@enlighten/design-system/dist/Grid';
import MenuItem from '@enlighten/design-system/dist/MenuItem';
import Stack from '@enlighten/design-system/dist/Stack';
import Typography from '@enlighten/design-system/dist/Typography';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import type { SiteSaleFormData } from '@/app/api/site-sale/route';
import Arrow from '@/assets/images/arrow.svg';
import FormItem from '@/components/pages/common/FormItem';
import { useOnChangeMessage } from '@/contexts/layouts/MessageProvider';
import toPhoneNumFormat from '@/utils/format';

const PLANT_STATUS_OPTIONS: { label: string; value: string }[] = [
	{ label: '토지확보', value: '토지확보' },
	{ label: '인허가중', value: '인허가중' },
	{ label: '착공대기', value: '착공대기' },
	{ label: '건설중', value: '건설중' },
	{ label: '계통대기', value: '계통대기' },
	{ label: '운영중', value: '운영중' },
];

const GUIDES = ['발전소 정보를 남겨주시면 매입 가능 여부와 예상 매입가를 검토 후 안내드립니다.', '설비용량 90kW 이상부터 발전소 매입 대상입니다.'];

const defaultValues: SiteSaleFormData = {
	name: '',
	mobile: '',
	capacity: '',
	plantStatus: '',
	address: '',
	email: '',
	content: '',
	privacyPolicyAgreement: false,
};

const Section7 = () => {
	const [loading, setLoading] = useState(false);

	const onChangeMessage = useOnChangeMessage();

	const { control, handleSubmit, reset } = useForm<SiteSaleFormData>({ defaultValues });

	const onSubmit = async (data: SiteSaleFormData) => {
		try {
			setLoading(true);
			const response = await fetch('/api/site-sale', {
				method: 'POST',
				body: JSON.stringify(data),
			});

			if (response.ok) {
				onChangeMessage({
					type: 'success',
					message: '문의가 등록되었습니다.',
					duration: 4000,
				});
				reset(defaultValues);
			} else {
				onChangeMessage({
					type: 'error',
					message: '문의 등록에 실패했습니다. 잠시 후 다시 시도해 주세요.',
					duration: 4000,
				});
			}
		} catch (e) {
			console.error(e);
			onChangeMessage({
				type: 'error',
				message: '문의 등록에 실패했습니다. 잠시 후 다시 시도해 주세요.',
				duration: 4000,
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Stack id="site-sale-form" alignItems="center" padding={{ xs: '60px 20px', md: '100px 24px 140px' }} bgcolor={{ xs: '#F6F3FF', md: '#F9F6FF' }} sx={{ scrollMarginTop: '80px' }}>
			<Stack
				position="relative"
				gap="40px"
				width="100%"
				maxWidth="680px"
				padding={{ xs: '40px 20px', md: '60px' }}
				bgcolor="common.white"
				border={{ xs: 'none', md: '1px solid #D6D7DA' }}
				borderColor="grey.400"
				borderRadius={{ xs: '14px', md: '16px' }}
				boxShadow={{ xs: '0 0 15px 0 rgba(0, 0, 0, 0.1)', md: '0 4px 14px 0 rgba(0, 0, 0, 0.1)' }}
			>
				<Stack gap={{ xs: '10px', md: '16px' }}>
					<Typography fontSize={{ xs: '24px', md: '40px' }} fontWeight="bold" lineHeight={{ xs: 1.4, md: '52px' }} textAlign="center" sx={{ wordBreak: 'keep-all' }}>
						발전소 매입 사전 신청서
					</Typography>
					<Stack component="ul" gap="2px" paddingLeft={{ xs: '21px', md: '24px' }} sx={{ listStyle: 'disc' }}>
						{GUIDES.map((guide) => (
							<Typography key={guide} component="li" fontSize={{ xs: '14px', md: '16px' }} lineHeight={{ xs: '24px', md: 1.4 }} color="grey.A200" sx={{ display: 'list-item', wordBreak: 'keep-all' }}>
								{guide}
							</Typography>
						))}
					</Stack>
				</Stack>

				<Stack gap={{ xs: '24px', md: '40px' }}>
					<Stack gap="20px">
						<Grid container spacing={{ xs: '40px', md: '20px' }}>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									control={control}
									name="name"
									rules={{ required: '성함을 입력해 주세요.' }}
									render={({ field: { value, onChange }, fieldState: { error } }) => (
										<FormItem fullWidth label="성함" required placeholder="홍길동" value={value} onChange={onChange} error={!!error} helperText={error?.message} />
									)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									control={control}
									name="mobile"
									rules={{
										required: '연락처를 입력해 주세요.',
										pattern: { value: /^[0-9]{2,3}[0-9]{3,4}[0-9]{4}$/, message: '올바른 형식으로 입력해 주세요.' },
									}}
									render={({ field: { value, onChange }, fieldState: { error } }) => (
										<FormItem
											fullWidth
											label="연락처"
											required
											placeholder="010-0000-0000"
											value={toPhoneNumFormat(value)}
											onChange={(e) => {
												onChange(e.target.value.replace(/-/g, ''));
											}}
											onKeyDown={(e) => {
												if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x', 'y', 'z'].includes(e.key)) {
													return;
												}
												if (!/^[0-9]$/.test(e.key) && !['Backspace', 'Delete', 'Shift', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
													e.preventDefault();
												}
											}}
											error={!!error}
											helperText={error?.message}
											slotProps={{ input: { inputProps: { maxLength: 13 } } }}
										/>
									)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									control={control}
									name="capacity"
									rules={{
										required: '설비용량을 입력해 주세요.',
										validate: (value) => Number(value) >= 90 || '설비용량 90kW 이상부터 매입 대상입니다.',
									}}
									render={({ field: { value, onChange }, fieldState: { error } }) => (
										<FormItem
											fullWidth
											label="설비용량 (kW)"
											required
											placeholder="예시 : 500"
											value={value}
											onChange={(e) => {
												onChange(
													e.target.value
														.replace(/[^0-9.]/g, '') // 숫자와 소수점만 허용
														.replace(/(\..*)\./g, '$1') // 소수점은 하나만
														.replace(/^0+(?=\d)/, '') // 앞자리 0 제거 (0.5 는 유지)
												);
											}}
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									control={control}
									name="plantStatus"
									rules={{ required: '발전소 상태를 선택해 주세요.' }}
									render={({ field: { value, onChange }, fieldState: { error } }) => (
										<FormItem
											select
											fullWidth
											label="발전소 상태"
											required
											value={value}
											onChange={onChange}
											error={!!error}
											helperText={error?.message}
											slotProps={{ select: { displayEmpty: true } }}
										>
											<MenuItem value="" disabled sx={{ display: 'none' }}>
												선택해 주세요
											</MenuItem>
											{PLANT_STATUS_OPTIONS.map((option) => (
												<MenuItem key={option.value} value={option.value}>
													{option.label}
												</MenuItem>
											))}
										</FormItem>
									)}
								/>
							</Grid>
							<Grid size={12}>
								<Controller
									control={control}
									name="address"
									rules={{ required: '발전소 주소를 입력해 주세요.' }}
									render={({ field: { value, onChange }, fieldState: { error } }) => (
										<FormItem fullWidth label="발전소 주소" required placeholder="예시 : 전남 해남군" value={value} onChange={onChange} error={!!error} helperText={error?.message} />
									)}
								/>
							</Grid>
							<Grid size={12}>
								<Controller
									control={control}
									name="email"
									rules={{
										required: '이메일 주소를 입력해 주세요.',
										pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: '이메일 형식이 올바르지 않습니다.' },
									}}
									render={({ field: { value, onChange }, fieldState: { error } }) => (
										<FormItem fullWidth label="이메일 주소" required placeholder="이메일을 입력해 주세요." value={value} onChange={onChange} error={!!error} helperText={error?.message} />
									)}
								/>
							</Grid>
							<Grid size={12}>
								<Controller
									control={control}
									name="content"
									rules={{
										maxLength: { value: 1000, message: '최대 1,000자까지 입력 가능합니다.' },
									}}
									render={({ field: { value, onChange }, fieldState: { error } }) => (
										<FormItem
											fullWidth
											label="요청사항"
											multiline
											minRows={3}
											placeholder="임대부지 / 종중부지 / 계통 미연계 등 특이사항이 있다면 함께 적어주세요."
											value={value}
											onChange={onChange}
											error={!!error}
											helperText={error?.message}
											slotProps={{ input: { sx: { padding: 0 }, inputProps: { maxLength: 1000 } } }}
										/>
									)}
								/>
							</Grid>
						</Grid>

						<Controller
							control={control}
							name="privacyPolicyAgreement"
							rules={{ required: '개인정보 수집 및 이용에 동의해 주세요.' }}
							render={({ field: { value, onChange }, fieldState: { error } }) => (
								<Box>
									<FormControlLabel
										control={<Checkbox />}
										checked={value}
										onChange={(_, checked) => {
											onChange(checked);
										}}
										label="상담을 위한 개인정보 수집 및 이용에 동의합니다."
										sx={{
											margin: 0,
											gap: '8px',
											'.MuiFormControlLabel-label': { fontSize: '14px', lineHeight: { xs: '24px', md: 1.5 }, color: 'grey.A200' },
										}}
									/>
									{error ? (
										<FormHelperText error sx={{ marginLeft: '32px' }}>
											{error.message}
										</FormHelperText>
									) : null}
								</Box>
							)}
						/>
					</Stack>

					<Button
						fullWidth
						disabled={loading}
						endIcon={loading ? <CircularProgress size={20} sx={{ color: 'grey.700' }} /> : <Arrow fill="white" />}
						onClick={handleSubmit(onSubmit)}
						sx={{
							height: 'auto',
							padding: { xs: '13px 24px', md: '16px 36px' },
							borderRadius: { xs: '4px', md: '6px' },
							fontSize: { xs: '16px', md: '18px' },
							lineHeight: { xs: '26px', md: '28px' },
							// 모바일 시안에는 화살표가 없음 (로딩 스피너는 유지)
							...(loading ? {} : { '& .MuiButton-endIcon': { display: { xs: 'none', md: 'inline-flex' } } }),
						}}
					>
						상담 신청하기
					</Button>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default Section7;
